import React, { useCallback } from 'react';
import DocumentTitle from '../../../shared/components/DocumentTitle';
import { useYourTitlesProducts } from '../../../api/local-content/your-titles/useYourTitlesProducts';
import { ProductSorting } from '../../../../models/Product';
import { useYourTitlesCreditSummary } from '../../../api/local-content/your-titles/useYourTitlesCreditsSummary';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import NoElementsFound, {
    NO_ELEMENTS_LC_TITLES,
    NO_ELEMENTS_LC_TITLES_FILTERED
} from '../../../shared/components/NoElementsFound';
import FilterUtils from '../../../shared/utils/utils-filter';
import ProductList from '../../../shared/common/ProductList';
import ProductElement from './ProductElement';
import Pagination from '../../../shared/common/Pagination';
import { LOCAL_CONTENT_SORT_BY, SortByDropDown } from '../../../shared/common/SortByDropDown';
import DisplayTypeDropDown from '../../../shared/common/DisplayTypeDropDown';
import { YourTitlesHeader } from './YourTitlesHeader';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchYourTitlesProductsAggregationsQuickSearch } from '../../../api/local-content/your-titles/fetchYourTitlesProductsAggregationsQuickSearch';
import { usePurchaseCredits } from '../../../api/local-content/your-titles/usePurchaseCredits';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchYourTitlesProductsAggregations } from '../../../api/local-content/your-titles/fetchYourTitlesProductsAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { useDefaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../shared/common/displayType/types';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { useAuthentication } from '../../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';

export const YourTitlesPage = () => {
    const { t } = useTranslation();
    const {
        spoc: { currency, taxName }
    } = useAuthentication();

    const { displayFilter, toggleDisplayFilter } = useDisplayFilterSearchParam();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultPageSize: 54,
        defaultSortBy: ProductSorting.TITLE_AZ
    });

    const { productFilter } = useProductFilters();
    const { display } = useDisplayTypeSearchParam(DISPLAY_TYPE_MINIMAL);

    //region FILTERBAR_V2
    const isFiltered = FilterUtils.hasActiveFilter(productFilter);
    const visibleFilterGroups = useDefaultVisibleFilterGroups();
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => ['yourTitlesPage', 'aggregation', filters, expandedFilterGroups],
        []
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchYourTitlesProductsAggregations(options),
        []
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        fetchYourTitlesProductsAggregationsQuickSearch,
        []
    );
    //endregion

    const { mutate: purchaseCreditsMutation } = usePurchaseCredits();

    const { products, isLoading: isProductsLoading } = useYourTitlesProducts({
        pagination: { page, pageSize, sortBy },
        filterMap: productFilter
    });

    const { data: creditsSummary, isLoading: isCreditsSummaryLoading } = useYourTitlesCreditSummary();

    const isLoading = isCreditsSummaryLoading || isProductsLoading;

    function handlePurchaseCredits(message: string) {
        purchaseCreditsMutation(message);
    }

    return (
        <DocumentTitle title={t('components.documentTitle.localContent')}>
            <ContainerInnerWithColumns>
                <LoadingOverlay loading={isLoading} />
                <YourTitlesHeader
                    taxName={taxName}
                    currency={currency}
                    credits={creditsSummary ?? {}}
                    purchaseCredits={handlePurchaseCredits}
                    products={products.list}
                />

                <>
                    <div className="filter-bar">
                        <div className="layout-column-33 fl-left">
                            <a onClick={toggleDisplayFilter} className="fl-left">
                                <i className="material-icons fl-left">list</i>
                                <div style={{ display: 'inline-block' }}> Filter</div>
                            </a>
                        </div>
                        {products.count === 0 && !isLoading ? null : (
                            <div className="layout-column-33 fl-right">
                                <SortByDropDown sorting={LOCAL_CONTENT_SORT_BY} sortBy={sortBy} />
                                <DisplayTypeDropDown displayType={display} excludeDisplayTypes={[DISPLAY_TYPE_TILE]} />
                            </div>
                        )}
                        <div className="layout-column-33 fl-left">
                            <Pagination totalItems={products.count} pageSize={pageSize} />
                        </div>
                    </div>
                </>

                <div className="content-area-inner flex-ca">
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                            isLocalContent={true}
                        />
                    ) : null}
                    {products.count === 0 && !isLoading ? (
                        <NoElementsFound type={isFiltered ? NO_ELEMENTS_LC_TITLES_FILTERED : NO_ELEMENTS_LC_TITLES} />
                    ) : (
                        <div className={classNames({ 'min-h-screen': isProductsLoading }, 'ca-main')}>
                            {products.count > 0 ? (
                                <div className="ca-main-inner">
                                    <ProductList
                                        products={products.list}
                                        displayType={display}
                                        onAddPrice={async () => {}}
                                    >
                                        {({ key, isListView, ...value }) => (
                                            <ProductElement {...value} key={key} displayType={display} />
                                        )}
                                    </ProductList>
                                    <Pagination totalItems={products.count} pageSize={pageSize} />
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </ContainerInnerWithColumns>
        </DocumentTitle>
    );
};
